import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Paper,
  MenuItem,
  Alert,
  Snackbar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { EntityStatus } from "../types";
import { CREATE_PRODUCT } from "../api/mutations/mutations";
import { graphqlRequest } from "../api/client";
import PageTitle from "../components/common/PageTitle";
import { resolveReturnDestination } from "../utils/navigationReturn";
import ProductRelationsPicker, {
  relationsSelectionToUuids,
  type ProductRelationsSelection,
} from "./product-manager/ProductRelationsPicker";

interface AddProductProps {
  tenantId: string | null;
  returnPath?: string;
  returnSearch?: string;
}

const emptyRelations = (): ProductRelationsSelection => ({
  apis: [],
  categories: [],
  documents: [],
  faqs: [],
  links: [],
  developerGuides: [],
});

const AddProduct: React.FC<AddProductProps> = ({
  tenantId,
  returnPath,
  returnSearch,
}) => {
  const navigate = useNavigate();
  const goBack = () =>
    navigate(resolveReturnDestination(returnPath, returnSearch, "products"));

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<EntityStatus>(EntityStatus.DRAFT);
  const [approvalNeeded, setApprovalNeeded] = useState<boolean>(false);
  const [relations, setRelations] = useState<ProductRelationsSelection>(
    emptyRelations,
  );

  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleAddProduct = async () => {
    setSubmitting(true);
    setErrorMessage(null);

    try {
      await graphqlRequest(CREATE_PRODUCT, {
        input: {
          tenantId,
          name,
          description,
          status,
          approvalNeeded,
          ...relationsSelectionToUuids(relations),
        },
      });

      setSuccessOpen(true);
      setTimeout(() => {
        goBack();
      }, 800);
    } catch (err: unknown) {
      console.error("Create product failed:", err);
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to create product",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageTitle
        pageTitle="Add Product"
        pageDescription="Create a new product and associate APIs, Categories, Links, Documents, and FAQs."
      />

      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Paper
          sx={{
            mt: 3,
            mb: 3,
            p: 3,
            maxWidth: "80%",
            border: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Description"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />

            <TextField
              select
              label="Tenant ID"
              value={tenantId}
              fullWidth
            >
              <MenuItem value={tenantId || ""}>{tenantId}</MenuItem>
            </TextField>

            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as EntityStatus)}
              fullWidth
            >
              {Object.values(EntityStatus).map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Needs approval"
              value={approvalNeeded ? "Yes" : "No"}
              onChange={(e) => setApprovalNeeded(e.target.value === "Yes")}
              fullWidth
              helperText="If Yes, subscription requests for this product require approval by a product owner."
            >
              <MenuItem value="No">No</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
            </TextField>

            <ProductRelationsPicker
              value={relations}
              onChange={setRelations}
              disabled={submitting}
            />

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleAddProduct}
                disabled={submitting}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {submitting ? "Creating..." : "Add Product"}
              </Button>
              <Button
                variant="outlined"
                onClick={goBack}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Snackbar open={successOpen} autoHideDuration={2000}>
        <Alert severity="success">Product created successfully</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddProduct;
